// INCONSISTENCY FLAGS (v1_695) — pure core, shared between the API (capture, at
// pre-submit) and the dashboard (render, in the Risk Factors panel), so both
// speak of one flag in exactly one shape. A genuine inconsistency Alex raises in
// the client interview (e.g. "active trading" vs a dormant filing history vs a
// stated turnover) and the client's explanation of it used to live ONLY in the
// chat transcript: never a tracked item, so it relied on the report model
// re-deriving it — which it does not, because pre-submit is told to drop anything
// the transcript already answers. An EXPLAINED-BUT-UNRESOLVED contradiction
// therefore vanished before it ever reached the MLRO.
//
// This captures it as a durable, MLRO-facing flag in collected_data (a sibling to
// requestItems, deliberately NOT client-facing and NOT reminder-driven — an
// inconsistency the client has already explained must never re-pester them). It
// is surfaced to the officer inside the existing Risk Factors acknowledge-with-note
// panel, where the MLRO judges it. The LLM that spots the inconsistency is the
// caller (pre-submit-review); this module is the pure, harnessed application.

// Stable dedupe key from the inconsistency text. Kept IDENTICAL to the dashboard's
// flagKey (lowercase, alphanumeric only, bounded) so the MLRO's acknowledgement —
// which the panel keys on the flag text via flagKey — lines up with the flag.
export function inconsistencyKey(text) {
  return String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 140);
}

function pick(obj, ...keys) {
  for (const k of keys) {
    const v = obj && obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

// Normalise one incoming record (from the model) into a flag, or null if it has
// no usable text. Accepts a few key spellings so the caller's prompt can stay
// natural ({point|text|inconsistency}, {clientExplanation|explanation}, resolved).
export function normInconsistency(rec, at) {
  if (!rec || typeof rec !== 'object') return null;
  const text = pick(rec, 'point', 'text', 'inconsistency', 'issue');
  if (!text) return null;
  const flag = {
    id: 'inc_' + inconsistencyKey(text).slice(0, 40),
    text,
    explanation: pick(rec, 'clientExplanation', 'explanation', 'note'),
    resolved: rec.resolved === true,
    at: at || new Date().toISOString()
  };
  // A4 (11/10/2026): the client's two accounts, in their own words, for the officer.
  const first = pick(rec, 'firstStatement'), later = pick(rec, 'laterStatement');
  if (first) flag.firstStatement = first;
  if (later) flag.laterStatement = later;
  return flag;
}

// Add-only merge into the stored list. The text of an existing flag is NEVER
// rephrased (that would orphan the MLRO's acknowledgement, which is keyed on it),
// and a flag is never removed here. An existing flag is only ENRICHED: it gains an
// explanation it did not have, and it may move false -> true on `resolved` (a
// contradiction the client has since cleared), never true -> false. Pure and
// idempotent: re-merging the same input changes nothing further.
export function mergeInconsistencyFlags(existing, incoming, opts = {}) {
  const at = opts.at || new Date().toISOString();
  const out = (Array.isArray(existing) ? existing : []).map(f => ({ ...f }));
  const byKey = new Map(out.map(f => [inconsistencyKey(f.text), f]));
  let added = 0, enriched = 0;
  for (const rec of (Array.isArray(incoming) ? incoming : [])) {
    const flag = normInconsistency(rec, at);
    if (!flag) continue;
    const key = inconsistencyKey(flag.text);
    const cur = byKey.get(key);
    if (!cur) {
      out.push(flag);
      byKey.set(key, flag);
      added++;
      continue;
    }
    let touched = false;
    if (!cur.explanation && flag.explanation) { cur.explanation = flag.explanation; touched = true; }
    if (!cur.firstStatement && flag.firstStatement) { cur.firstStatement = flag.firstStatement; touched = true; }
    if (!cur.laterStatement && flag.laterStatement) { cur.laterStatement = flag.laterStatement; touched = true; }
    if (!cur.resolved && flag.resolved) { cur.resolved = true; touched = true; }
    if (touched) enriched++;
  }
  return { flags: out, added, enriched, changed: added + enriched };
}

// Map a stored flag to the officer Risk Factors panel's flag model. The panel
// renders {text, detail, ack, source}; the client's explanation is the detail,
// and `source: 'interview'` lets the panel tag it as raised in the interview. The
// MLRO's acknowledgement is attached by the caller (findAck against flagAcks,
// keyed on the text) — never rephrase `text` or that lookup breaks.
export function inconsistencyFlagModel(flag) {
  if (!flag || !flag.text) return null;
  const resolvedLine = flag.resolved
    ? 'The client offered an explanation that appears to resolve this; recorded for your confirmation.'
    : 'The client offered an explanation but it may not fully resolve the point; your judgement is needed.';
  const said = (flag.firstStatement ? `First said: ${flag.firstStatement}\n` : '') + (flag.laterStatement ? `Later said: ${flag.laterStatement}\n` : '');
  const detail = (said ? said + '\n' : '') + (flag.explanation ? `Client's explanation: ${flag.explanation}\n\n` : '') + resolvedLine;
  return { text: flag.text, detail, source: 'interview' };
}
